import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Bell, Menu } from 'lucide-react';
import { clsx } from 'clsx';
import { ThemeMenu } from './ThemeMenu';
import { Sidebar } from './Sidebar';
import { DownloadsMenu } from '@/components/exports/DownloadsMenu';
import { findNavItem } from '@/security/navigation';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCompanyStore } from '@/stores/useCompanyStore';

export function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { pathname } = useLocation();
  const user = useAuthStore((state) => state.user);
  const loadCompanies = useCompanyStore((state) => state.load);

  // Sociétés du sélecteur, rechargées à chaque changement d'utilisateur connecté
  useEffect(() => {
    if (user) void loadCompanies(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seule l'identité compte, pas un changement de nom
  }, [user?.urn, loadCompanies]);

  const page = findNavItem(pathname);
  const title = page?.label ?? 'Teiji';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="bg-card border-b border-slate-200 lg:hidden px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-semibold text-slate-900">{title}</span>
          <div className="flex items-center">
            <ThemeMenu />
            <DownloadsMenu />
          </div>
        </header>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between px-8 py-5 bg-card border-b border-slate-200 sticky top-0 z-20">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
            {page?.description && <p className="text-slate-500 mt-1">{page.description}</p>}
          </div>

          <div className="relative flex items-center gap-1">
            <ThemeMenu />
            <DownloadsMenu />
            <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              aria-label="Notifications"
              className={clsx(
                'relative p-2 rounded-full transition-colors',
                isNotificationsOpen
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
              )}
            >
              <Bell className="w-5 h-5" />
            </button>

            {isNotificationsOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsNotificationsOpen(false)} />
                <div className="absolute right-0 mt-2 w-80 bg-card rounded-xl shadow-lg border border-slate-200 z-20 overflow-hidden">
                  <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                    <h3 className="font-semibold text-sm text-slate-900">Notifications</h3>
                  </div>
                  {/* TODO : brancher sur le notification-bundle */}
                  <p className="p-4 text-sm text-slate-500 text-center">Aucune notification</p>
                </div>
              </>
            )}
          </div>
        </div>

        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="lg:hidden mb-6">
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            {page?.description && <p className="text-sm text-slate-500 mt-1">{page.description}</p>}
          </div>

          <div className="max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
